import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';

import { formatWithPattern, getUserTimezone } from '@repo/common';

import type { NewPrimaryMethod } from './mfa-authenticator-manage';
import { AuthenticatorAppIcon, EmailCodeMethodIcon, PasskeyMethodIcon, SmsMethodIcon } from './mfa-method-icons';

export function figmaReplacementChoices(
  excluding: NewPrimaryMethod,
  t: TFunction<'common'>,
  details: {
    email?: string;
    passkeyCount: number;
    /** Lists SMS: the account has it enrolled, or SMS setup is offered. */
    smsOffered?: boolean;
    smsPhoneMasked?: string;
    totpEnrolled?: boolean;
  },
): ReadonlyArray<{ description: string; icon: ReactNode; title: string; value: NewPrimaryMethod }> {
  const totp = {
    description: details.totpEnrolled ? t('security.mfa.totpEnrolledHint') : t('security.mfa.totpSetupHint'),
    icon: <AuthenticatorAppIcon />,
    title: t('security.mfa.totpTitle'),
    value: 'totp' as const,
  };
  const sms = {
    description: details.smsPhoneMasked
      ? t('security.mfa.smsEnrolledHint', { masked: details.smsPhoneMasked })
      : t('security.mfa.smsSetupHint'),
    icon: <SmsMethodIcon />,
    title: t('security.mfa.smsMethodTitle'),
    value: 'sms' as const,
  };
  const email = {
    description: details.email || t('security.mfa.emailSetupHint'),
    icon: <EmailCodeMethodIcon />,
    title: t('security.mfa.emailMethodTitle'),
    value: 'email' as const,
  };
  const passkey = {
    description:
      details.passkeyCount > 0
        ? t('security.mfa.passkeyEnrolledHint', { count: details.passkeyCount })
        : t('security.mfa.passkeySetupHint'),
    icon: <PasskeyMethodIcon />,
    title: t('security.mfa.passkeyMethodTitle'),
    value: 'passkey' as const,
  };
  const all = { totp, sms, email, passkey };
  const order: NewPrimaryMethod[] = ['totp', 'sms', 'email', 'passkey'];
  const isOffered = (method: NewPrimaryMethod) => method !== excluding && (method !== 'sms' || details.smsOffered);
  return order.flatMap(method => (isOffered(method) ? [all[method]] : []));
}

export function formatMfaFactorDate(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  return formatWithPattern(value, 'd MMMM yyyy', getUserTimezone());
}
